import { playNextAd } from "./ads-manager.js";
import { createLogger,PLAYER_LOG_EVENTS, logPlayerEvent } from "../helpers/logger.js";

export function createAdSequencer(player, state, options = {}) {
  const {
    playLock,
    debug = false,
    onFallbackBreakFailed = null,
  } = options;
  const log = createLogger("AdSequencer", debug);

  const attemptTimeoutMs = options.attemptTimeoutMs ?? 8000;

  let activeAttemptId = 0;
  let handlingResult = false;
  let attemptTimer = null;

  function clearAttemptTimer() {
    if (attemptTimer) {
      clearTimeout(attemptTimer);
      attemptTimer = null;
    }
  }

  function resetAttemptFlags() {
    handlingResult = false;
    clearAttemptTimer();
  }

  function nextAttemptId() {
    activeAttemptId += 1;
    resetAttemptFlags();
    return activeAttemptId;
  }

  function isActiveAttempt(attemptId) {
    return !!attemptId && attemptId === activeAttemptId;
  }

  function getUrls(type) {
    return state[`${type}Urls`] || [];
  }

  function getIndexKey(type) {
    return `${type}Index`;
  }

  function getCurrentIndex(type) {
    return state[getIndexKey(type)] || 0;
  }

  function hasMoreUrls(type) {
    return getCurrentIndex(type) < getUrls(type).length;
  }

  function setCurrentAttempt(type, attemptId) {
    state.__currentAdType = type;
    state.__currentAdAttemptId = attemptId;
    state.__currentAdStarted = false;
    state.__currentAdFinished = false;
  }

  function clearCurrentAttempt() {
    state.__currentAdType = null;
    state.__currentAdAttemptId = null;
    state.__currentAdStarted = false;
    state.__currentAdFinished = false;
    player.__adPlaying = false;
    resetAttemptFlags();
  }

  function unlockContentAfterBreak(type) {
    player.__lockContent = false;
    player.__adPlaying = false;

    if (type === "fallback") {
      state.ompAdsDone = true;
      player.off("play", playLock);
      player.controls(true);
    }

    if (type !== "postroll" && state.hasContentVideo) {
      player.play().catch((e) => {
        log.debug("content play after ad break failed", e);
      });
    }
  }

  function finishBreak(type, reason) {
    log.debug("finishBreak", {
      type,
      reason,
      currentSrc: player.currentSrc?.(),
    });

    clearCurrentAttempt();
    unlockContentAfterBreak(type);
  }

  function failBreak(type, reason) {
    log.debug("failBreak", {
      type,
      reason,
      currentSrc: player.currentSrc?.(),
      adBreakHadAdStarted: !!state.__adBreakHadAdStarted,
    });

    const hadAdStarted = !!state.__adBreakHadAdStarted;

    clearCurrentAttempt();

    if (
      type === "fallback" &&
      typeof onFallbackBreakFailed === "function"
    ) {

      const shouldDestroy = !state.adsOptional;
      
      onFallbackBreakFailed({
        type,
        reason,
        waterfallExhausted: true,
        hadAdStarted,
        shouldDestroy,
      });

      if (shouldDestroy) {
        return;
      }
    }

    unlockContentAfterBreak(type);
  }

  function tryNextUrl(type, reason) {
    clearAttemptTimer();
    
    player.__lockContent = !state.adsOptional;
    player.__adPlaying = false;

    if (!hasMoreUrls(type)) {
      log.debug("no more ad urls", {
        type,
        reason,
        index: getCurrentIndex(type),
        total: getUrls(type).length,
      });

      failBreak(type, "no-more-urls");
      return;
    }

    const attemptId = nextAttemptId();
    const urls = getUrls(type);
    const index = getCurrentIndex(type);
    const currentUrl = urls[index] || null;
    setCurrentAttempt(type, attemptId);

    log.debug("tryNextUrl", {
      type,
      reason,
      attemptId,
      index: getCurrentIndex(type),
      total: getUrls(type).length,
    });

    
    logPlayerEvent(log, PLAYER_LOG_EVENTS.NEXT_PROVIDER_CALLED, {
      type,
      reason,
      attemptId,
      index,
      total: urls.length,
      url: currentUrl,
    });

    logPlayerEvent(log, PLAYER_LOG_EVENTS.REQUEST_ATTEMPT, {
      type,
      attemptId,
      index,
      total: urls.length,
      url: currentUrl,
    });

    const requestStarted = playNextAd(player, type, state, false, debug);

    if (!requestStarted) {
      log.debug("ad request did not start, moving to next url", {
        type,
        attemptId,
        index,
        total: urls.length,
      });

      tryNextUrl(type, "request-not-started");
      return;
    }

    attemptTimer = setTimeout(() => {
      if (!isActiveAttempt(attemptId) || handlingResult) return;

      log.debug("ad attempt timeout, moving to next url", {
        type,
        attemptId,
        timeoutMs: attemptTimeoutMs,
      });

      handleAttemptResult("ad-timeout", {
        message: "ad attempt timeout",
      });
    }, attemptTimeoutMs);
  }

  function getAdErrorCode(event) {
    try {
      return (
        event?.data?.AdError?.getErrorCode?.() ??
        event?.data?.adError?.getErrorCode?.() ??
        event?.data?.adsRequestError?.getErrorCode?.() ??
        event?.error?.getErrorCode?.() ??
        event?.adsError?.getErrorCode?.() ??
        null
      );
    } catch {
      return null;
    }
  }

  function getAdErrorMessage(event) {
    try {
      return (
        event?.data?.AdError?.toString?.() ??
        event?.data?.adError?.toString?.() ??
        event?.data?.adsRequestError?.toString?.() ??
        event?.error?.toString?.() ??
        event?.adsError?.toString?.() ??
        event?.message ??
        null
      );
    } catch {
      return null;
    }
  }

  function handleAttemptResult(kind, event = null) {
    const type = state.__currentAdType;
    const attemptId = state.__currentAdAttemptId;

    const errorCode = getAdErrorCode(event);
    const errorMessage = getAdErrorMessage(event);

    log.debug("handleAdResult", {
      kind,
      type,
      attemptId,
      activeAttemptId,
      currentSrc: player.currentSrc?.(),
      adPlaying: player.__adPlaying,
      adStarted: state.__currentAdStarted,
      adFinished: state.__currentAdFinished,
      index: type ? getCurrentIndex(type) : null,
      total: type ? getUrls(type).length : null,
      errorCode,
      errorMessage,
    });

    logPlayerEvent(log, PLAYER_LOG_EVENTS.RESPONSE_RECEIVED, {
      kind,
      type,
      attemptId,
      activeAttemptId,
      currentSrc: player.currentSrc?.(),
      adStarted: state.__currentAdStarted,
      adFinished: state.__currentAdFinished,
      errorCode,
      errorMessage,
    });

    if (!type || !attemptId) return;

    if (!isActiveAttempt(attemptId)) {
      log.debug("stale attempt ignored", { kind, type, attemptId, activeAttemptId });
      return;
    }

    if (handlingResult) {
      log.debug("already handling result, ignored", { kind, type, attemptId });
      return;
    }

    const isError =
      kind === "adserror" ||
      kind === "ads-ad-error" ||
      kind === "ad-timeout";

    const isEnded =
      kind === "adend" ||
      kind === "ads-ad-ended";

    const endedBeforeStart = isEnded && !state.__currentAdStarted;

    if (!isError && !isEnded) return;

    handlingResult = true;
    clearAttemptTimer();
    state.__currentAdFinished = true;
    player.__adPlaying = false;

    if (isError || endedBeforeStart) {
      const failReason = endedBeforeStart ? `${kind}-before-start` : kind;

      log.debug("ad attempt failed, moving to next url", {
        type,
        attemptId,
        reason: failReason,
        index: getCurrentIndex(type),
        total: getUrls(type).length,
      });

      logPlayerEvent(log, PLAYER_LOG_EVENTS.NO_AD, {
        type,
        attemptId,
        reason: failReason,
        index: getCurrentIndex(type),
        total: getUrls(type).length,
        errorCode,
        errorMessage,
      });

      tryNextUrl(type, failReason);
      return;
    }

    if (isEnded) {
      log.debug("ad attempt ended successfully, break complete", {
        type,
        attemptId,
      });

      finishBreak(type, kind);
    }
  }

  function markStarted() {
    const type = state.__currentAdType;
    const attemptId = state.__currentAdAttemptId;

    if (!type || !attemptId) {
      log.debug("ads-ad-started with no active attempt");
      return;
    }

    if (!isActiveAttempt(attemptId)) {
      log.debug("ads-ad-started for stale attempt ignored", {
        type,
        attemptId,
        activeAttemptId,
      });
      return;
    }

    clearAttemptTimer();

    state.__currentAdStarted = true;
    state.__adBreakHadAdStarted = true;
    player.__adPlaying = true;


    if (type === "fallback") {
      state.__fallbackHadAdStarted = true;
    }
    
    log.debug("ads-ad-started", {
      type,
      attemptId,
      currentSrc: player.currentSrc?.(),
    });
  }

  function startAdBreak(type) {
    state.__adBreakHadAdStarted = false;

    log.debug("startAdBreak", {
      type,
      index: getCurrentIndex(type),
      total: getUrls(type).length,
      currentSrc: player.currentSrc?.(),
    });

    logPlayerEvent(log, PLAYER_LOG_EVENTS.WATERFALL_STARTED, {
      type,
      index: getCurrentIndex(type),
      total: getUrls(type).length,
      urls: getUrls(type),
    });

    tryNextUrl(type, "start-break");
  }

  const onAdsAdEnded = (e) => handleAttemptResult("ads-ad-ended", e);
  const onAdEnd = (e) => handleAttemptResult("adend", e);
  const onAdError = (e) => handleAttemptResult("adserror", e);
  const onAdsAdError = (e) => handleAttemptResult("ads-ad-error", e);

  player.on("ads-ad-started", markStarted);
  player.on("ads-ad-ended", onAdsAdEnded);
  player.on("adend", onAdEnd);
  player.on("adserror", onAdError);
  player.on("ads-ad-error", onAdsAdError);

  return startAdBreak;
}